import React, { FC } from "react";
import {
	Box,
	Typography,
	Button,
	IconButton,
	TextField,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	Tooltip,
	Divider,
	Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	RewardEngineOption,
	TreatmentSegment,
	CmVisitScheduleOption,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import {
	appendSegment,
	clearOverride,
	hasSegments,
	isOverrideActive,
	isResetLadderLocked,
	removeSegment,
	resolveResetLadderValue,
	rewardEngineName,
	segmentHeaderSummary,
	setOverride,
	updateSegment,
	validateSegmentContiguity,
	SegmentOverrideField,
} from "./segmentEditor.helpers";

/**
 * SIMS-7151 (FR-3) + SIMS-7159 (FR-4, FR-6) — plan-level segment accordion editor for the
 * Treatment Plan template form.
 *
 * Structural fields (week range, visits/week, min hours between visits) are always editable (7151).
 * Per SIMS-7159 each segment additionally exposes toggle-driven overrides (reward engine, behavior,
 * visit schedule, disenrollment threshold): OFF inherits the template default (shown as a read-only
 * hint, nothing persisted); ON persists the override to segments[n]. resetLadderOnEntry is locked to
 * true when the segment's effective reward engine differs from the previous segment's (a reward-
 * engine change resets the ladder, FR-6), otherwise it is freely configurable. Contiguity validation
 * is SIMS-7167. Controlled: `segments` in / `onChange` out.
 *
 * This is a component index (excluded from unit-coverage by jest.config); the testable derivation
 * logic lives in ./segmentEditor.helpers.ts and is jest-tested. Synthetic data only — no PHI.
 */
export interface SegmentTemplateDefaults {
	rewardEngineId?: string;
	behavior?: string;
	cmVisitScheduleOptionIds?: string;
	disenrollmentDaysThreshold?: number;
}

interface SegmentEditorProps {
	segments: TreatmentSegment[];
	onChange: (segments: TreatmentSegment[]) => void;
	rewardEngineOptions: RewardEngineOption[];
	behaviors: Event[];
	cmVisitScheduleOptions: CmVisitScheduleOption[];
	/** Template defaults — the effective values a segment inherits when its override is OFF. */
	templateDefaults?: SegmentTemplateDefaults;
}

const SegmentEditor: FC<SegmentEditorProps> = ({
	segments,
	onChange,
	rewardEngineOptions,
	behaviors,
	cmVisitScheduleOptions,
	templateDefaults = {},
}) => {
	const addSegment = (): void => onChange(appendSegment(segments));

	const patchNumber = (
		index: number,
		field: "visitsPerWeek" | "minHoursBetweenVisits",
		raw: string,
	): void => {
		const value = raw === "" ? 0 : Number(raw);
		onChange(updateSegment(segments, index, { [field]: value }));
	};

	const patchWeek = (
		index: number,
		edge: "start" | "end",
		raw: string,
	): void => {
		const value = raw === "" ? 0 : Number(raw);
		const current = segments[index].weekRange || { start: 0, end: 0 };
		onChange(
			updateSegment(segments, index, {
				weekRange: { ...current, [edge]: value },
			}),
		);
	};

	// Toggle an override ON (seed with the template default so it starts valid) or OFF (inherit).
	const toggleOverride = (
		index: number,
		field: SegmentOverrideField,
		on: boolean,
	): void => {
		if (!on) {
			onChange(clearOverride(segments, index, field));
			return;
		}
		const seed =
			field === "disenrollmentDaysThreshold"
				? (templateDefaults.disenrollmentDaysThreshold ?? 0)
				: (templateDefaults[field] ?? "");
		onChange(setOverride(segments, index, field, seed));
	};

	const behaviorName = (id?: string): string =>
		behaviors.find((b) => b._id === id)?.name ?? "None";

	const scheduleDisplay = (ids?: string): string => {
		if (!ids) {
			return "None";
		}
		return ids
			.split(",")
			.filter(Boolean)
			.map(
				(id) =>
					cmVisitScheduleOptions.find(
						(o) => o.cmVisitScheduleOptions.toString() === id,
					)?.cmVisitScheduleOptionsDaysDisplay ?? id,
			)
			.join(", ");
	};

	// A single toggle-driven override row: header switch + (when ON) the input, else the inherited hint.
	const overrideRow = (
		index: number,
		field: SegmentOverrideField,
		label: string,
		hint: string,
		input: React.ReactNode,
	): React.ReactNode => {
		const active = isOverrideActive(segments[index], field);
		return (
			<Grid item xs={12} md={6} key={field}>
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={active}
							onChange={(e) => toggleOverride(index, field, e.target.checked)}
						/>
					}
					label={`Override ${label}`}
				/>
				{active ? (
					input
				) : (
					<Typography variant="caption" color="text.secondary" display="block">
						Inherits template default: {hint}
					</Typography>
				)}
			</Grid>
		);
	};

	// SIMS-7167: the first contiguity violation across the whole list. Resolved once
	// per render rather than inside the per-segment map (SIMS-7487 tidy-up: the call
	// scans every segment, so calling it per accordion made rendering O(N^2)).
	const contiguityError = validateSegmentContiguity(segments);

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
			data-testid="segment-editor"
		>
			<Typography variant="subtitle1" fontWeight="bold">
				Program Segments
			</Typography>

			{!hasSegments(segments) ? (
				<Typography variant="body2" color="text.secondary">
					No segments defined. This is a single-phase program. Add a segment to
					define a multi-segment phase structure.
				</Typography>
			) : (
				segments.map((segment, index) => {
					// SIMS-7167: surface the first contiguity violation inline on the offending segment.
					const hasContiguityError = contiguityError?.index === index;
					const resetLocked = isResetLadderLocked(
						segments,
						index,
						templateDefaults.rewardEngineId,
					);
					const resetValue = resolveResetLadderValue(
						segments,
						index,
						templateDefaults.rewardEngineId,
					);
					return (
						<Accordion key={index} defaultExpanded={false}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography sx={{ flexGrow: 1 }}>
									{segmentHeaderSummary(
										segment,
										index,
										rewardEngineOptions,
										templateDefaults.rewardEngineId,
									)}
								</Typography>
								<IconButton
									size="small"
									aria-label={`Remove segment ${index + 1}`}
									component="span"
									onClick={(e) => {
										e.stopPropagation();
										onChange(removeSegment(segments, index));
									}}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={2}>
									{hasContiguityError && (
										<Grid item xs={12}>
											<Alert severity="error">{contiguityError?.message}</Alert>
										</Grid>
									)}
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Start Week"
											type="number"
											required
											error={hasContiguityError}
											value={segment.weekRange?.start ?? ""}
											onChange={(e) =>
												patchWeek(index, "start", e.target.value)
											}
											inputProps={{ min: 1 }}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="End Week"
											type="number"
											required
											value={segment.weekRange?.end ?? ""}
											onChange={(e) => patchWeek(index, "end", e.target.value)}
											inputProps={{ min: 1 }}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Visits Per Week"
											type="number"
											required
											value={segment.visitsPerWeek ?? ""}
											onChange={(e) =>
												patchNumber(index, "visitsPerWeek", e.target.value)
											}
											inputProps={{ min: 1 }}
										/>
									</Grid>
									<Grid item xs={6} md={3}>
										<TextField
											fullWidth
											label="Min Hours Between Visits"
											type="number"
											required
											value={segment.minHoursBetweenVisits ?? ""}
											onChange={(e) =>
												patchNumber(
													index,
													"minHoursBetweenVisits",
													e.target.value,
												)
											}
											inputProps={{ min: 0 }}
										/>
									</Grid>

									<Grid item xs={12}>
										<Divider textAlign="left">
											<Typography variant="caption" color="text.secondary">
												Overrides (optional) — off inherits the template default
											</Typography>
										</Divider>
									</Grid>

									{overrideRow(
										index,
										"rewardEngineId",
										"reward engine",
										rewardEngineName(
											templateDefaults.rewardEngineId,
											rewardEngineOptions,
										),
										<FormControl fullWidth size="small">
											<InputLabel>Reward Engine</InputLabel>
											<Select
												label="Reward Engine"
												value={segment.rewardEngineId ?? ""}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"rewardEngineId",
															e.target.value,
														),
													)
												}
											>
												{rewardEngineOptions.map((engine) => (
													<MenuItem key={engine.id} value={engine.id}>
														{engine.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"behavior",
										"behavior",
										behaviorName(templateDefaults.behavior),
										<FormControl fullWidth size="small">
											<InputLabel>Behavior</InputLabel>
											<Select
												label="Behavior"
												value={segment.behavior ?? ""}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"behavior",
															e.target.value,
														),
													)
												}
											>
												{behaviors.map((b) => (
													<MenuItem key={b._id} value={b._id}>
														{b.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"cmVisitScheduleOptionIds",
										"visit schedule",
										scheduleDisplay(templateDefaults.cmVisitScheduleOptionIds),
										<FormControl fullWidth size="small">
											<InputLabel>Visit Schedule</InputLabel>
											<Select
												multiple
												label="Visit Schedule"
												value={
													segment.cmVisitScheduleOptionIds
														? segment.cmVisitScheduleOptionIds
																.split(",")
																.filter(Boolean)
														: []
												}
												onChange={(e) =>
													onChange(
														setOverride(
															segments,
															index,
															"cmVisitScheduleOptionIds",
															(e.target.value as string[]).join(","),
														),
													)
												}
												renderValue={(selected) =>
													scheduleDisplay((selected as string[]).join(","))
												}
											>
												{cmVisitScheduleOptions.map((o) => (
													<MenuItem
														key={o.cmVisitScheduleOptions}
														value={o.cmVisitScheduleOptions.toString()}
													>
														{o.cmVisitScheduleOptionsDaysDisplay}
													</MenuItem>
												))}
											</Select>
										</FormControl>,
									)}

									{overrideRow(
										index,
										"disenrollmentDaysThreshold",
										"disenrollment threshold",
										templateDefaults.disenrollmentDaysThreshold != null
											? `${templateDefaults.disenrollmentDaysThreshold} days`
											: "None",
										<TextField
											fullWidth
											size="small"
											label="Disenrollment Days Threshold"
											type="number"
											value={segment.disenrollmentDaysThreshold ?? ""}
											onChange={(e) =>
												onChange(
													setOverride(
														segments,
														index,
														"disenrollmentDaysThreshold",
														e.target.value === "" ? 0 : Number(e.target.value),
													),
												)
											}
											inputProps={{ min: 1 }}
										/>,
									)}

									<Grid item xs={12}>
										<Tooltip
											title={
												resetLocked
													? "Locked on: this segment's reward engine differs from the previous segment's, so the ladder resets on entry (FR-6)."
													: ""
											}
											disableHoverListener={!resetLocked}
										>
											<FormControlLabel
												control={
													<Switch
														size="small"
														checked={resetValue}
														disabled={resetLocked}
														onChange={(e) =>
															onChange(
																updateSegment(segments, index, {
																	resetLadderOnEntry: e.target.checked,
																}),
															)
														}
													/>
												}
												label="Reset ladder on entry"
											/>
										</Tooltip>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					);
				})
			)}

			<Box>
				<Button
					size="small"
					variant="outlined"
					startIcon={<AddIcon />}
					onClick={addSegment}
				>
					Add Segment
				</Button>
			</Box>
		</Box>
	);
};

export default SegmentEditor;
